import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, MessageSquareText, Smartphone, Check, ChevronDown, Loader2, Copy } from "lucide-react";
import {
  CONTACT_CHANNELS,
  EMAIL_TEMPLATES,
  FOLLOW_UP_OPTIONS,
  INSTAGRAM_DM_TEMPLATES,
  SCHEDULE_OPTIONS,
  buildEmailTemplate,
  buildInstagramTemplate,
  appendInstagramInvitePlaceholderToBody,
  expandInstagramInviteForSend,
} from "./contactTemplates";
import { campaignAdvertisingService } from "../../../../services/campaignAdvertisingService";
import { socialAccountsService } from "../../../../services/companyAuthService";
import { pipelineService } from "../../../../services/pipelineService";

const SOCIAL_ACCOUNTS_PATH = "/company/dashboard/social-accounts";

function InstagramConnectGateModal({ open, onClose, onGoToSocialAccounts }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        role="presentation"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="instagram-gate-title"
        className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div id="instagram-gate-title" className="text-sm md:text-lg font-bold text-gray-900">
              Connect Instagram first
            </div>
            <p className="mt-1 text-xs md:text-sm text-gray-600">
              Link your brand&apos;s Instagram account in Infuber before sending Instagram DMs from the
              pipeline.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-2 md:px-5 py-4 flex flex-row md:flex-row gap-2 md:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs md:text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onGoToSocialAccounts}
            className="px-4 py-2 rounded-xl bg-orange-500 text-white text-xs md:text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm"
          >
            Go to Social accounts
          </button>
        </div>
      </div>
    </div>
  );
}
// NOTE: Instagram DM is implemented via deep linking (ig.me), not auto-send API.

function ModalShell({ open, title, onClose, children, blockClose = false }) {
  useEffect(() => {
    if (!open || blockClose) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, blockClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[65]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        role="presentation"
        onClick={() => {
          if (!blockClose) onClose?.();
        }}
      />
      <div className="absolute inset-x-0 top-[80px] bottom-[56px] lg:bottom-0 flex items-center justify-center overflow-y-auto p-2 sm:p-4">
        <div className="relative w-full max-w-xl max-h-full rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-lg font-bold text-gray-900 truncate">{title}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={blockClose}
              className="w-8 h-8 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center justify-center shrink-0 disabled:opacity-50 disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="md:px-6 px-2 md:py-4 py-2 overflow-y-auto flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ContactHeaderCard({ influencer }) {
  const initials = useMemo(() => {
    const name = influencer?.name ?? "";
    const parts = String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "—";
  }, [influencer?.name]);

  return (
    <div className="rounded-xl bg-gray-50 border border-gray-100 p-2 flex items-center">
      <div className="lg:w-11 lg:h-11 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
        <span className="lg:text-sm text-xs font-bold text-orange-700">{initials}</span>
      </div>
      <div className="min-w-0">
        <div className="lg:text-sm text-xs font-bold text-gray-900 truncate">
          {influencer?.name ?? "Influencer"}
        </div>
        <div className="lg:text-xs text-xs text-gray-500 truncate">
          {influencer?.handle ?? "@handle"} · {influencer?.followersLabel ? `· ${influencer?.followersLabel}` : ""}
        </div>
      </div>
    </div>
  );
}

function ChannelOption({ icon: Icon, title, subtitle, selected, disabled, loading, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "rounded-xl border px-2 lg:px-3 py-2 lg:py-3 text-left transition-all w-full",
        disabled
          ? "bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed"
          : selected
            ? "bg-orange-50 border-orange-400 shadow-sm"
            : "bg-white border-gray-200 hover:bg-gray-50",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            "lg:w-8 lg:h-8 w-6 h-6 rounded-xl flex items-center justify-center border shrink-0",
            disabled
              ? "bg-white border-gray-200"
              : selected
                ? "bg-orange-100 border-orange-200"
                : "bg-gray-50 border-gray-100",
          ].join(" ")}
        >
          {loading ? (
            <Loader2 className="lg:w-5 lg:h-5 w-4 h-4 text-orange-600 animate-spin" aria-hidden />
          ) : (
            <Icon
              className={[
                "lg:w-5 lg:h-5 w-4 h-4",
                disabled ? "text-gray-400" : selected ? "text-orange-700" : "text-gray-700",
              ].join(" ")}
            />
          )}
        </div>
        <div className="min-w-0">
          <div className="lg:text-sm text-xs font-bold text-gray-900">{title}</div>
          <div className="lg:text-xs text-xs text-gray-500 mt-1">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function PillTabs({ tabs, activeId, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2 lg:gap-3">
      {tabs.map((t) => {
        const active = t.id === activeId;
        const disabled = Boolean(t.disabled);
        return (
          <button
            key={t.id}
            type="button"
            disabled={disabled}
            aria-disabled={disabled}
            title={disabled ? String(t.disabledHint || "Coming soon") : undefined}
            onClick={() => {
              if (!disabled) onChange(t.id);
            }}
            className={[
              "px-2 lg:px-4 py-1 lg:py-2 rounded-full border text-[10px] lg:text-sm font-semibold transition-colors",
              disabled
                ? "relative cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500 pointer-events-none select-none"
                : active
                  ? "bg-orange-50 text-orange-700 border-orange-200"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
            ].join(" ")}
          >
            <span
              className={[
                "inline-flex items-center gap-1",
                disabled ? "blur-[2.5px] opacity-55" : "",
              ].join(" ")}
            >
              {t.label}
            </span>
            {disabled ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-2 text-[9px] font-extrabold uppercase tracking-wide text-gray-600">
                Coming Soon
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled }) {
  return (
    <div className="min-w-0 relative">
      {label ? <div className="lg:text-xs text-xs lg:text-sm font-semibold text-gray-500 mb-1">{label}</div> : null}
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none px-2 lg:px-3 py-1 lg:py-2 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300 disabled:bg-gray-50 disabled:text-gray-500"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-[36px] w-4 h-4 text-gray-500" />
    </div>
  );
}

function Banner({ icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-2 text-xs lg:text-sm text-purple-900 flex items-start gap-3">
      {/* <div className="w-9 h-9 rounded-xl bg-white/60 border border-purple-200 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-purple-700" />
      </div> */}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SuccessScreen({ onDone, channelLabel }) {
  return (
    <div className="py-4">
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <div className="mt-4 text-2xl font-extrabold text-gray-900">
          {channelLabel === "Email" ? "Email sent!" : "Instagram DM sent!"}
        </div>
        <div className="mt-1 text-sm text-gray-500">
          {channelLabel === "Email"
            ? "Message sent successfully. Open & click tracking enabled."
            : "Instagram DM sent successfully. Open & click tracking enabled."}
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-gray-50 border border-gray-100 p-4">
        <div className="text-xs lg:text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
          What&apos;s next
        </div>
        <ul className="text-xs lg:text-sm text-gray-700 space-y-1">
          <li>
            • Card moved to{" "}
            <span className="font-semibold">
              {channelLabel === "Email" ? "Contacted" : "Connected"}
            </span>{" "}
            stage
          </li>
          {channelLabel === "Email" ? (
            <li>• Email tracking enabled — you&apos;ll be notified on open</li>
          ) : (
            <li>
              • Next step: negotiate pricing, finalize the contract, and move to{" "}
              <span className="font-semibold text-gray-900">Confirmed</span> stage
            </li>
          )}
        </ul>
      </div>

      {/* <div className="mt-4 flex items-center justify-end gap-2 lg:gap-3">
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-2 rounded-xl bg-orange-500 text-white text-xs lg:text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm"
        >
          Done
        </button>
      </div> */}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {{ id: string, name: string, handle: string, followers?: number } | null} props.influencer
 * @param {string} props.campaignId
 * @param {string} props.campaignName
 * @param {string} props.brandName
 * @param {string | null} [props.pipelineItemId] — Mongo pipeline item id (for Infuber invite link on Instagram DM).
 * @param {() => void} props.onClose
 * @param {string} [props.initialChannel]
 * @param {string} [props.initialEmailTemplateId]
 * @param {number} [props.initialStep]
 * @param {(payload: { channel: string, templateId: string, scheduleId: string, followUpId: string, subject?: string, body: string, advertisingId?: string, confirmed?: boolean }) => boolean | Promise<boolean>} props.onSend — resolve `true` only after a successful send (shows success step).
 */
export default function ContactWizardModal({
  open,
  influencer,
  pipelineItemId,
  campaignId,
  campaignName,
  brandName,
  onClose,
  initialChannel,
  initialEmailTemplateId,
  initialStep,
  onSend,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedInviteLink, setCopiedInviteLink] = useState(false);
  const [dmInviteUrl, setDmInviteUrl] = useState("");
  const [dmInviteToken, setDmInviteToken] = useState("");
  const [dmInviteLoading, setDmInviteLoading] = useState(false);
  const [dmInviteError, setDmInviteError] = useState(/** @type {string | null} */(null));
  const [dmInviteRetryToken, setDmInviteRetryToken] = useState(0);
  const [instagramGateModalOpen, setInstagramGateModalOpen] = useState(false);
  const [checkingInstagramGate, setCheckingInstagramGate] = useState(false);
  const channelRef = useRef("");
  useEffect(() => {
    channelRef.current = channel;
  }, [channel]);

  const [igTemplateId, setIgTemplateId] = useState(INSTAGRAM_DM_TEMPLATES.COLLAB_INTRO);
  const [emailTemplateId, setEmailTemplateId] = useState(EMAIL_TEMPLATES.PARTNERSHIP_PROPOSAL);

  const [scheduleId, setScheduleId] = useState("now");
  const [followUpId, setFollowUpId] = useState("off");

  const [advertisingOptions, setAdvertisingOptions] = useState(
    /** @type {Array<{ id: string, label: string }>} */([]),
  );
  const [advertisingLoading, setAdvertisingLoading] = useState(false);
  const [selectedAdvertisingId, setSelectedAdvertisingId] = useState("");

  const computed = useMemo(() => {
    const influencerName = influencer?.name ?? "";
    if (channel === CONTACT_CHANNELS.EMAIL) {
      const selectedAd = advertisingOptions.find((o) => o.id === selectedAdvertisingId) || null;
      const advertisingLabel = selectedAd?.label || "";
      const advertisingUrl = selectedAd?.id
        ? `https://www.infuber.com/campaign/${encodeURIComponent(selectedAd.id)}`
        : "";
      const tpl = buildEmailTemplate({
        templateId: emailTemplateId,
        influencerName,
        brandName,
        campaignName,
        advertisingLabel,
        advertisingUrl,
      });
      return { subject: tpl.subject, body: tpl.body };
    }
    if (channel !== CONTACT_CHANNELS.INSTAGRAM_DM) {
      return { subject: "", body: "" };
    }
    let body = buildInstagramTemplate({
      templateId: igTemplateId,
      influencerName,
      brandName,
      campaignName,
    });
    if (
      igTemplateId === INSTAGRAM_DM_TEMPLATES.COLLAB_INTRO &&
      String(dmInviteUrl || "").trim()
    ) {
      body = appendInstagramInvitePlaceholderToBody(body);
    }
    return { subject: "", body };
  }, [
    channel,
    igTemplateId,
    emailTemplateId,
    influencer?.name,
    brandName,
    campaignName,
    advertisingOptions,
    selectedAdvertisingId,
    dmInviteUrl,
  ]);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open) {
      setSending(false);
      setInstagramGateModalOpen(false);
      setCheckingInstagramGate(false);
      return;
    }
    const stepToUse = Number.isFinite(Number(initialStep)) ? Number(initialStep) : 1;
    setStep(stepToUse === 2 || stepToUse === 3 ? stepToUse : 1);
    setSending(false);
    setChannel(initialChannel ? String(initialChannel) : "");
    setIgTemplateId(INSTAGRAM_DM_TEMPLATES.COLLAB_INTRO);
    setEmailTemplateId(
      initialEmailTemplateId ? String(initialEmailTemplateId) : EMAIL_TEMPLATES.PARTNERSHIP_PROPOSAL
    );
    setScheduleId("now");
    setFollowUpId("off");
    setAdvertisingOptions([]);
    setSelectedAdvertisingId("");
    setCopied(false);
    setCopiedInviteLink(false);
    setDmInviteUrl("");
    setDmInviteToken("");
    setDmInviteLoading(false);
    setDmInviteError(null);
    setDmInviteRetryToken(0);
  }, [open, initialChannel, initialEmailTemplateId, initialStep]);

  useEffect(() => {
    if (!open) return;
    if (channel !== CONTACT_CHANNELS.INSTAGRAM_DM) return;
    setIgTemplateId(INSTAGRAM_DM_TEMPLATES.COLLAB_INTRO);
  }, [open, channel]);

  useEffect(() => {
    if (!open || !pipelineItemId || channel !== CONTACT_CHANNELS.INSTAGRAM_DM || step !== 2) {
      return;
    }
    let cancelled = false;
    setDmInviteLoading(true);
    setDmInviteError(null);
    (async () => {
      try {
        const data = await pipelineService.ensureInstagramDmInviteLink({
          itemId: pipelineItemId,
        });
        if (cancelled) return;
        setDmInviteUrl(String(data?.inviteUrl || "").trim());
        setDmInviteToken(String(data?.token || "").trim());
      } catch (err) {
        if (cancelled) return;
        setDmInviteUrl("");
        setDmInviteToken("");
        setDmInviteError(
          err?.response?.data?.message ||
          err?.message ||
          "Could not load invitation link"
        );
      } finally {
        if (!cancelled) setDmInviteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pipelineItemId, channel, step, dmInviteRetryToken]);

  useEffect(() => {
    if (!open) return;
    if (initialChannel !== CONTACT_CHANNELS.INSTAGRAM_DM) return;
    let cancelled = false;
    setCheckingInstagramGate(true);
    (async () => {
      try {
        const data = await socialAccountsService.getInstagramStatus();
        if (cancelled) return;
        if (!data?.connected && channelRef.current === CONTACT_CHANNELS.INSTAGRAM_DM) {
          setChannel("");
          setInstagramGateModalOpen(true);
        }
      } catch {
        if (
          !cancelled &&
          channelRef.current === CONTACT_CHANNELS.INSTAGRAM_DM
        ) {
          setChannel("");
          setInstagramGateModalOpen(true);
        }
      } finally {
        if (!cancelled) setCheckingInstagramGate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialChannel]);

  useEffect(() => {
    if (!open || channel !== CONTACT_CHANNELS.EMAIL || !campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        setAdvertisingLoading(true);
        // Show all advertisings for this campaign (company can decide whether to attach).
        const response = await campaignAdvertisingService.getCampaignAdvertisings(campaignId);
        const items = Array.isArray(response?.data) ? response.data : [];
        const nextOptions = items.map((item) => ({
          id: String(item._id),
          label:
            String(item.requirement || item.campaignId?.name || "Advertising campaign").slice(
              0,
              80,
            ) + (String(item.requirement || "").length > 80 ? "…" : ""),
        }));
        if (!cancelled) {
          setAdvertisingOptions(nextOptions);
          // Default: attach nothing unless the user selects an advertising campaign.
          setSelectedAdvertisingId("");
        }
      } catch (error) {
        // Surface only in console to avoid blocking invite flow
        // eslint-disable-next-line no-console
        console.error("Failed to load advertising campaigns for pipeline email:", error);
        if (!cancelled) {
          setAdvertisingOptions([]);
          setSelectedAdvertisingId("");
        }
      } finally {
        if (!cancelled) {
          setAdvertisingLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, channel, campaignId]);

  useEffect(() => {
    setSubject(computed.subject ?? "");
    setBody(computed.body ?? "");
  }, [computed.subject, computed.body]);

  useEffect(() => {
    if (scheduleId !== "custom") return;
    window.setTimeout(() => {
      setScheduleId("now");
    }, 0);
  }, [scheduleId]);

  const followersLabel = useMemo(() => {
    const n = influencer?.followers;
    if (!n || Number.isNaN(Number(n))) return "";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M followers`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K followers`;
    return `${n} followers`;
  }, [influencer?.followers]);

  const influencerForHeader = useMemo(() => {
    if (!influencer) return null;
    return { ...influencer, followersLabel };
  }, [influencer, followersLabel]);

  const canChooseTemplate = useMemo(() => {
    if (!channel) return false;
    if (channel === CONTACT_CHANNELS.WHATSAPP) return false;
    return true;
  }, [channel]);

  const onChooseTemplate = useCallback(() => {
    if (!canChooseTemplate) return;
    setStep(2);
  }, [canChooseTemplate]);

  const onSelectChannel = useCallback(
    async (nextChannel) => {
      if (sending) return;
      if (nextChannel === CONTACT_CHANNELS.INSTAGRAM_DM) {
        setCheckingInstagramGate(true);
        try {
          const data = await socialAccountsService.getInstagramStatus();
          if (!data?.connected) {
            setInstagramGateModalOpen(true);
            return;
          }
        } catch {
          setInstagramGateModalOpen(true);
          return;
        } finally {
          setCheckingInstagramGate(false);
        }
      }
      setChannel(nextChannel);
    },
    [sending]
  );

  const onCloseInstagramGate = useCallback(() => {
    setInstagramGateModalOpen(false);
  }, []);

  const onGoToSocialAccounts = useCallback(() => {
    setInstagramGateModalOpen(false);
    onClose?.();
    navigate(SOCIAL_ACCOUNTS_PATH);
  }, [navigate, onClose]);

  const onBack = useCallback(() => {
    if (sending) return;
    setStep(1);
  }, [sending]);

  const onSendNow = useCallback(async () => {
    if (sending) return;
    if (!channel) return;
    const templateId =
      channel === CONTACT_CHANNELS.EMAIL ? emailTemplateId : igTemplateId;
    const payload = {
      channel,
      templateId,
      scheduleId,
      followUpId,
      subject: channel === CONTACT_CHANNELS.EMAIL ? subject : undefined,
      body,
      advertisingId: channel === CONTACT_CHANNELS.EMAIL ? selectedAdvertisingId || undefined : undefined,
    };

    if (!onSend) {
      setStep(channel === CONTACT_CHANNELS.INSTAGRAM_DM ? 3 : 4);
      return;
    }

    setSending(true);
    try {
      const result = await onSend(payload);
      if (result === true) {
        setStep(channel === CONTACT_CHANNELS.INSTAGRAM_DM ? 3 : 4);
      }
    } finally {
      setSending(false);
    }
  }, [
    onSend,
    sending,
    channel,
    emailTemplateId,
    igTemplateId,
    scheduleId,
    followUpId,
    subject,
    body,
    selectedAdvertisingId,
  ]);

  const onConfirmInstagramDmSent = useCallback(async () => {
    if (sending) return;
    if (!pipelineItemId) return;
    setSending(true);
    try {
      await pipelineService.confirmInstagramDmSent({ itemId: pipelineItemId, token: dmInviteToken });
      // Inform parent (board) so it can update local stage/activity immediately.
      if (onSend) {
        try {
          await onSend({
            channel: CONTACT_CHANNELS.INSTAGRAM_DM,
            templateId: igTemplateId,
            scheduleId,
            followUpId,
            body: expandInstagramInviteForSend(body, dmInviteUrl),
            confirmed: true,
          });
        } catch {
          // Ignore parent handler errors; backend already persisted stage + activity.
        }
      }
      setStep(4);
    } catch (err) {
      // Keep user on confirmation screen; allow retry via Yes.
      // eslint-disable-next-line no-console
      console.error("Failed to confirm Instagram DM send:", err);
    } finally {
      setSending(false);
    }
  }, [sending, pipelineItemId, dmInviteToken, onSend, igTemplateId, scheduleId, followUpId, body, dmInviteUrl]);

  const channelLabel =
    channel === CONTACT_CHANNELS.EMAIL ? "Email" : "Instagram DM";

  const igUsername = useMemo(() => {
    const raw = influencer?.handle ?? influencer?.username ?? "";
    return String(raw).trim().replace(/^@/, "");
  }, [influencer?.handle, influencer?.username]);

  const igDeepLink = useMemo(() => {
    if (!igUsername) return "";
    return `https://ig.me/m/${encodeURIComponent(igUsername)}`;
  }, [igUsername]);

  const copyDmMessageToClipboard = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        const out = expandInstagramInviteForSend(body, dmInviteUrl);
        await navigator.clipboard.writeText(out);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // clipboard can fail due to permissions; ignore
    }
  }, [body, dmInviteUrl]);

  const copyInviteLinkToClipboard = useCallback(async () => {
    if (!dmInviteUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(dmInviteUrl);
        setCopiedInviteLink(true);
        window.setTimeout(() => setCopiedInviteLink(false), 2000);
      }
    } catch {
      // ignore
    }
  }, [dmInviteUrl]);

  const openInstagramDM = useCallback(async () => {
    if (!igDeepLink) return;
    await copyDmMessageToClipboard();
    window.open(igDeepLink, "_blank", "noopener,noreferrer");
  }, [igDeepLink, copyDmMessageToClipboard]);

  const canOpenInstagramDm = Boolean(
    igDeepLink &&
    pipelineItemId &&
    dmInviteUrl &&
    dmInviteToken &&
    !dmInviteLoading &&
    !dmInviteError
  );

  return (
    <>
      <InstagramConnectGateModal
        open={instagramGateModalOpen}
        onClose={onCloseInstagramGate}
        onGoToSocialAccounts={onGoToSocialAccounts}
      />
      <ModalShell
        open={open}
        onClose={onClose}
        blockClose={sending}
        title={`Contact — ${influencer?.name ?? "Influencer"}`}
      >
        {step === 1 ? (
          <div className="space-y-2 lg:space-y-5">
            <ContactHeaderCard influencer={influencerForHeader} />

            <div className="text-sm font-semibold text-gray-700">
              Choose contact channel:
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 lg:gap-3">
              <ChannelOption
                icon={Smartphone}
                title="Instagram DM"
                subtitle={
                  checkingInstagramGate ? "Checking connection…" : "Open in Instagram"
                }
                selected={channel === CONTACT_CHANNELS.INSTAGRAM_DM}
                loading={checkingInstagramGate}
                disabled={sending || checkingInstagramGate}
                onClick={() => onSelectChannel(CONTACT_CHANNELS.INSTAGRAM_DM)}
              />
              <ChannelOption
                icon={Mail}
                title="Email"
                subtitle="Track opens & replies"
                selected={channel === CONTACT_CHANNELS.EMAIL}
                onClick={() => onSelectChannel(CONTACT_CHANNELS.EMAIL)}
              />
              <ChannelOption
                icon={MessageSquareText}
                title="WhatsApp"
                subtitle="Business API"
                selected={channel === CONTACT_CHANNELS.WHATSAPP}
                disabled
                onClick={() => { }}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 lg:gap-3 lg:pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs lg:text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onChooseTemplate}
                disabled={sending || !canChooseTemplate}
                className="px-4 py-2 rounded-xl bg-orange-500 text-white text-xs lg:text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                Choose Template →
              </button>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-2 lg:space-y-5">
            {channel === CONTACT_CHANNELS.INSTAGRAM_DM ? (
              <>
                <Banner icon={Smartphone}>
                  <div className="font-bold">Instagram DM (manual) · Deep link</div>
                  <div className="text-purple-800/80 text-sm mt-1">
                    We&apos;ll open the Instagram chat for{" "}
                    <span className="font-semibold">@{igUsername || "username"}</span>. The draft shows{" "}
                    <span className="font-semibold">Invitation link</span> instead of the long URL; when you copy or open,
                    the real Infuber link is inserted so it stays tappable in Instagram. Paste into the chat, then tap
                    Send.
                  </div>
                </Banner>

                {!pipelineItemId ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    This contact is missing pipeline context. Close the modal and open Contact again from the pipeline
                    board.
                  </div>
                ) : null}

                {dmInviteLoading ? (
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin text-orange-600" aria-hidden />
                    Preparing secure Infuber invitation link…
                  </div>
                ) : null}

                {dmInviteError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span>{dmInviteError}</span>
                    <button
                      type="button"
                      onClick={() => setDmInviteRetryToken((n) => n + 1)}
                      className="shrink-0 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-[11px] font-bold text-red-800 hover:bg-red-50"
                    >
                      Retry
                    </button>
                  </div>
                ) : null}

                <div className="text-sm font-semibold text-gray-700">Template:</div>
                <PillTabs
                  tabs={[
                    { id: INSTAGRAM_DM_TEMPLATES.COLLAB_INTRO, label: "Collab Intro" },
                    {
                      id: INSTAGRAM_DM_TEMPLATES.CAMPAIGN_BRIEF,
                      label: "Campaign Brief",
                      disabled: true,
                      disabledHint: "Template coming soon for Instagram DM",
                    },
                    {
                      id: INSTAGRAM_DM_TEMPLATES.FOLLOW_UP_NUDGE,
                      label: "Follow-up Nudge",
                      disabled: true,
                      disabledHint: "Template coming soon for Instagram DM",
                    },
                  ]}
                  activeId={igTemplateId}
                  onChange={setIgTemplateId}
                />

                <textarea
                  value={body}
                  disabled={sending}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full min-h-[160px] md:min-h-[190px] lg:min-h-[220px] rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm leading-6 outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300 disabled:bg-gray-50"
                />

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={copyDmMessageToClipboard}
                    disabled={sending || !body}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    Copy message
                  </button>
                  <button
                    type="button"
                    onClick={copyInviteLinkToClipboard}
                    disabled={sending || !dmInviteUrl}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    Copy invite link
                  </button>
                </div>
                {copied ? (
                  <div className="text-xs font-semibold text-emerald-700">Message copied to clipboard</div>
                ) : null}
                {copiedInviteLink ? (
                  <div className="text-xs font-semibold text-emerald-700">Invite link copied</div>
                ) : null}

                <div className="rounded-xl border border-gray-200 bg-white p-3 text-xs text-gray-700">
                  <div className="font-bold text-gray-900">Campaign invitation (Infuber)</div>
                  <div className="mt-1 text-gray-600">
                    {dmInviteLoading ? (
                      "…"
                    ) : dmInviteUrl ? (
                      <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-1">
                        <span>Open in dashboard:</span>
                        <a
                          href={dmInviteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-orange-600 underline underline-offset-2 hover:text-orange-700"
                        >
                          Invitation link
                        </a>
                      </span>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <SelectField
                    label="Schedule"
                    value={scheduleId}
                    disabled={sending}
                    onChange={setScheduleId}
                    options={SCHEDULE_OPTIONS}
                  />
                  <SelectField
                    label="Auto follow-up if no reply"
                    value={followUpId}
                    disabled={sending}
                    onChange={setFollowUpId}
                    options={FOLLOW_UP_OPTIONS}
                  />
                </div>

                <div className="flex items-center justify-between gap-2 lg:gap-3 pt-2 lg:pt-3 ">
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={sending}
                    className="px-2 py-1 lg:px-6 lg:py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs lg:text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <span aria-hidden>←</span> Back
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await openInstagramDM();
                      setStep(3);
                    }}
                    disabled={sending || !canOpenInstagramDm}
                    className="px-2 py-2 md:px-6 md:py-3 rounded-xl bg-orange-500 text-white text-xs lg:text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm inline-flex items-center justify-center gap-2 min-w-[148px] disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        Opening…
                      </>
                    ) : (
                      "Open Instagram DM"
                    )}
                  </button>
                </div>
              </>
            ) : null}

            {channel === CONTACT_CHANNELS.EMAIL ? (
              <>
                <div className="text-sm font-semibold text-gray-700">Template:</div>
                <PillTabs
                  tabs={[
                    { id: EMAIL_TEMPLATES.PARTNERSHIP_PROPOSAL, label: "Partnership Proposal" },
                    { id: EMAIL_TEMPLATES.FOLLOW_UP_EMAIL, label: "Follow-up Email" },
                  ]}
                  activeId={emailTemplateId}
                  onChange={setEmailTemplateId}
                />

                <div className="space-y-3">
                  <div>
                    <SelectField
                      label="Advertising campaign (optional)"
                      value={selectedAdvertisingId || ""}
                      disabled={sending || advertisingLoading}
                      onChange={setSelectedAdvertisingId}
                      options={[
                        { id: "", label: advertisingLoading ? "Loading…" : "Select advertising campaign" },
                        ...advertisingOptions,
                      ]}
                    />
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-2 lg:p-4">
                    <div className="text-xs font-semibold text-gray-500 mb-2">Subject (fixed):</div>
                    <div className="w-full rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                      {subject}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">Body (fixed):</div>
                    <div className="w-full min-h-[160px] md:min-h-[190px] lg:min-h-[220px] rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm leading-6 text-gray-800 whitespace-pre-wrap">
                      {body}
                    </div>
                  </div>

                  <div>
                    <SelectField
                      label="Schedule"
                      value={scheduleId}
                      disabled={sending}
                      onChange={setScheduleId}
                      options={SCHEDULE_OPTIONS}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 lg:gap-3 pt-2 lg:pt-3">
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={sending}
                    className="px-2 py-1 md:px-6 md:py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs lg:text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <span aria-hidden>←</span> Back
                  </button>
                  <button
                    type="button"
                    onClick={onSendNow}
                    disabled={sending}
                    className="px-2 py-2 md:px-6 md:py-3 rounded-xl bg-orange-500 text-white text-xs lg:text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm inline-flex items-center justify-center gap-2 min-w-[148px] disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Mail className="lg:w-4 lg:h-4 w-3 h-3" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (
          channel === CONTACT_CHANNELS.INSTAGRAM_DM ? (
            <div className="py-4 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="text-sm font-extrabold text-gray-900">Confirm Instagram DM sent</div>
                <div className="mt-2 text-sm text-gray-600">
                  Have you successfully sent the invitation message to{" "}
                  <span className="font-semibold">@{igUsername || "username"}</span> on Instagram?
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  We&apos;ll only move this influencer to <span className="font-semibold">Connected</span> after you
                  confirm.
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  disabled={sending}
                  onClick={() => setStep(2)}
                  className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs lg:text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  No, go back
                </button>
                <button
                  type="button"
                  disabled={sending || !pipelineItemId}
                  onClick={onConfirmInstagramDmSent}
                  className="px-4 py-2 rounded-xl bg-orange-500 text-white text-xs lg:text-sm font-bold hover:bg-orange-600 transition-colors shadow-sm disabled:opacity-60 disabled:pointer-events-none"
                >
                  Yes, I sent it
                </button>
              </div>
            </div>
          ) : null
        ) : null}

        {step === 4 ? (
          <SuccessScreen
            channelLabel={channelLabel}
            onDone={() => {
              onClose?.();
            }}
          />
        ) : null}
      </ModalShell>
    </>
  );
}

